import React, { useCallback, useState } from 'react';

import { useRouter } from 'next/router';
import styled from 'styled-components';

import useAsset from '~/techstyle-shared/react-assets/src/useAsset';
import { mobile, useBreakpoint } from '~/techstyle-shared/react-components';

import { useGridFacets } from '../../context/GridDataContexts';
import useDiscoveryGuideScrollbar from '../../hooks/useDiscoveryGuideScrollbar';
import useGridInputs, { GRID_TYPES } from '../../hooks/useGridInputs';
import {
  isFacetOptionSelected,
  updateFiltersInUrlParams,
} from '../../utils/filters';
import { QueryParamBehavior } from '../../utils/routing';
import { isValidVideoSrc } from '../../utils/video';

import { HORIZONTAL_PADDING, HORIZONTAL_PADDING_MOBILE } from './constants';
import DiscoveryGuideCard from './DiscoveryGuideCard';
import DiscoveryGuideControls from './DiscoveryGuideControls';

const CenteringWrapper = styled.div<{ $shouldUseFlexStart: boolean }>`
  width: ${({ $shouldUseFlexStart }) =>
    $shouldUseFlexStart ? '100%' : 'fit-content'};
  max-width: 100%;
  margin: 0 auto;
`;

const FiltersRow = styled.div<{
  $shouldUseFlexStart: boolean;
  $hasControls: boolean;
}>`
  display: flex;
  gap: 8px;
  overflow-x: ${({ $shouldUseFlexStart }) =>
    $shouldUseFlexStart ? 'auto' : 'visible'};
  padding: ${({ $hasControls }) => ($hasControls ? '3px' : '32px')}
    ${HORIZONTAL_PADDING}px 32px;
  scrollbar-width: none;
  -ms-overflow-style: none;

  &::-webkit-scrollbar {
    display: none;
  }

  ${({ $hasControls }) => mobile`
    padding: ${
      $hasControls ? '3px' : '32px'
    } ${HORIZONTAL_PADDING_MOBILE}px 32px;
  `}
`;

const Separator = styled.div`
  border-bottom: 1px solid ${({ theme }) => theme.colors.gridV2FilterSeparator};
`;

const DiscoveryGuideContainer = styled.div``;

const DiscoveryGuide = () => {
  const { isMobile } = useBreakpoint();
  const { allRegisteredFacets, selectedUrlFilters } = useGridFacets();
  const router = useRouter();
  const [expandedTitles, setExpandedTitles] = useState<Set<string>>(
    () => new Set()
  );
  const [isPlayingAllVideos, setIsPlayingAllVideos] = useState(true);
  const [playbackEpochMs] = useState(() => Date.now());

  const { gridType, discoveryGuide } = useGridInputs();
  const {
    aggregationId,
    cmsAssetContainer,
    label,
    numFeatures,
    options: builderOptions,
    showTitleOnly,
    showDescription,
  } = discoveryGuide || {};

  const activeCmsAssetContainer = isMobile
    ? cmsAssetContainer?.mobile
    : cmsAssetContainer?.desktop;

  const assetQueryOptions = { multi: true } as any;

  const assetResult = useAsset(activeCmsAssetContainer, assetQueryOptions);
  const { networkStatus, data } = assetResult || {};
  const hasFetchedAssets =
    !activeCmsAssetContainer ||
    (networkStatus?.isUpToDate && !networkStatus?.isLoading);
  const isFetchingAssets = !hasFetchedAssets;

  const toggleExpandedTitle = useCallback((value: string) => {
    setExpandedTitles((prev) => {
      const next = new Set(prev);

      if (next.has(value)) {
        next.delete(value);
      } else {
        next.add(value);
      }

      return next;
    });
  }, []);

  const toggleFilterInUrl = useCallback(
    ({ facetName, facetOptionValue }) => {
      updateFiltersInUrlParams({
        facetName,
        facetOption: facetOptionValue,
        router,
        queryParamBehavior: QueryParamBehavior.TOGGLE,
      });
    },
    [router]
  );

  // Match the configured DG facet against the facets available for this grid.
  const registeredFacet = allRegisteredFacets?.find(
    (facet) => facet.name === aggregationId
  );

  // Only render builder options that exist in the facet response and are not hidden.
  const visibleFacetOptions =
    builderOptions?.filter(
      (builderOption) =>
        registeredFacet?.options?.some(
          (option) => option.value === builderOption.value
        ) && !builderOption.hideOnDiscoveryGuide
    ) || [];

  const hasVideoAssets = visibleFacetOptions.some((facetOption) => {
    const matchingAsset = data?.assets?.find(
      (item) => item.options?.customVars?.tag_name === facetOption.value
    );
    return isValidVideoSrc(matchingAsset?.imageFilename);
  });

  const {
    filtersRowRef,
    scrollbarThumbRef,
    hasOverflow,
    thumbWidthPercent,
    handleRailScroll,
    canScrollLeft,
    canScrollRight,
    isScrollbarThumbReady,
    scrollRailByStep,
  } = useDiscoveryGuideScrollbar(visibleFacetOptions.length);

  const handleTogglePlayPause = useCallback(() => {
    setIsPlayingAllVideos((previousValue) => !previousValue);
  }, []);

  const handleScrollLeft = useCallback(
    () => scrollRailByStep('left'),
    [scrollRailByStep]
  );
  const handleScrollRight = useCallback(
    () => scrollRailByStep('right'),
    [scrollRailByStep]
  );

  // DG is not applicable for the Search Grid, ignore loading skeleton state
  if (gridType === GRID_TYPES.CONSTRUCTOR_SEARCH_GRID || !discoveryGuide) {
    return null;
  }

  if (!registeredFacet) {
    return null;
  }

  if (!visibleFacetOptions.length) {
    return null;
  }

  const shouldUseFlexStart = isMobile || hasOverflow;
  const hasControls = hasOverflow || hasVideoAssets || !!label;

  return (
    <DiscoveryGuideContainer>
      <CenteringWrapper $shouldUseFlexStart={shouldUseFlexStart}>
        <DiscoveryGuideControls
          hasOverflow={hasOverflow}
          hasVideoAssets={hasVideoAssets}
          isPlayingAllVideos={isPlayingAllVideos}
          label={label}
          onTogglePlayPause={handleTogglePlayPause}
          scrollbarThumbRef={scrollbarThumbRef}
          thumbWidthPercent={thumbWidthPercent}
          isScrollbarThumbReady={isScrollbarThumbReady}
          canScrollLeft={canScrollLeft}
          canScrollRight={canScrollRight}
          onScrollLeft={handleScrollLeft}
          onScrollRight={handleScrollRight}
        />
        <FiltersRow
          ref={filtersRowRef}
          $shouldUseFlexStart={shouldUseFlexStart}
          $hasControls={hasControls}
          onScroll={handleRailScroll}
        >
          {visibleFacetOptions.map(
            ({ value, title, description, features }) => {
              const isExpanded = !showTitleOnly && expandedTitles.has(value);
              const isSelected = isFacetOptionSelected({
                facetName: aggregationId,
                facetOption: value,
                selectedUrlFilters,
              });
              const matchingAsset = data?.assets?.find(
                (item) => item.options?.customVars?.tag_name === value
              );

              return (
                <DiscoveryGuideCard
                  key={value}
                  value={value}
                  title={title}
                  description={description}
                  features={features}
                  asset={matchingAsset}
                  numFeatures={numFeatures}
                  showTitleOnly={showTitleOnly}
                  showDescription={showDescription}
                  isExpanded={isExpanded}
                  isSelected={isSelected}
                  isFetchingAssets={isFetchingAssets}
                  isPlayingAllVideos={isPlayingAllVideos}
                  playbackEpochMs={playbackEpochMs}
                  onToggleExpanded={() => toggleExpandedTitle(value)}
                  onToggleFilter={() =>
                    toggleFilterInUrl({
                      facetName: aggregationId,
                      facetOptionValue: value,
                    })
                  }
                />
              );
            }
          )}
        </FiltersRow>
      </CenteringWrapper>
      <Separator />
    </DiscoveryGuideContainer>
  );
};

// Memoize so products-driven re-renders in the parent grid view don't cascade
// into this component. DiscoveryGuide only subscribes to FacetsContext.
export default React.memo(DiscoveryGuide);
